import { HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';

import { ACCESS_TOKEN_SPLIT_THRESHOLD, StorageToken } from '../../../shared/configs/storage-token.config';
import { UserRole } from '../../../shared/configs/user-role.config';
import { HelperService } from '../helper/helper.service';
import { AuthService } from '../http/auth/auth.service';
import { StorageService } from '../storage/storage.service';
import { UserService } from '../user/user.service';

import { JwtHelperService } from '@auth0/angular-jwt';

@Injectable({
  providedIn: 'root',
})
export class MasqueradeService {
  jwtHelperService!: JwtHelperService;

  constructor(
    private helperService: HelperService,
    private router: Router,
    private storageService: StorageService,
    private userService: UserService,
    private authService: AuthService
  ) {
    this.jwtHelperService = new JwtHelperService();
  }

  hasMasqueradeAccess(toBeMasqueradedUserRoles: string[]): boolean {
    const loggedInUserRoles = this.userService.getUserRoles();

    if (loggedInUserRoles?.includes(UserRole.admin)) {
      return true;
    }
    if (loggedInUserRoles?.includes(UserRole.ITAdmin) && !toBeMasqueradedUserRoles.includes(UserRole.admin)) {
      return true;
    }
    if (loggedInUserRoles?.includes(UserRole.MIS) && toBeMasqueradedUserRoles.includes(UserRole.centerManager)) {
      return true;
    }

    return false;
  }

  getUserDetails(): void {
    this.userService.getUserDetails$()?.subscribe(() => {
      this.isMasqueraded();
    });
  }

  getPreviousAccessToken(): string {
    const token = this.storageService.getCookie(StorageToken.previousAccessToken);
    if (token) {
      return token;
    }
    // Large-token case: `previous_access_token_1/_2` (NOT `access_token_1/_2` — those belong to
    // whichever session is CURRENTLY active and would collide with the masquerade target's token).
    const part1 = this.storageService.getCookie('previous_access_token_1');
    const part2 = this.storageService.getCookie('previous_access_token_2');

    return part1 && part2 ? part1 + part2 : '';
  }

  isMasqueraded(): boolean {
    let fetchIsMasqueraded = this.storageService.getCookie(StorageToken.masquerade);
    let isMasqueraded = fetchIsMasqueraded ? atob(fetchIsMasqueraded) : 'false';

    return isMasqueraded == 'true';
  }

  masqueradeAsOtherUser(token: string): void {
    this.setPreviousAccessToken();
    const encodedData = btoa('true');
    this.storageService.addCookie(StorageToken.masquerade, encodedData, {
      path: '/',
      secure: window.location.protocol === 'https:',
      sameSite: 'Lax',
    });
    this.clearAllCookies();
    this.setAccessToken(token);
    this.setUserDetails(token);
    const userDetails = this.jwtHelperService.decodeToken(token);
    if (userDetails) {
      const first = userDetails.given_name || '';
      const last =
        userDetails.family_name && userDetails.family_name !== 'NULL' && userDetails.family_name !== 'undefined'
          ? userDetails.family_name
          : '';
      const name = `${first} ${last}`.trim() || userDetails.preferred_username || userDetails.username || 'user';

      this.helperService.showMessage('success', `Masqueraded As ${name}`);
    }

    this.router.navigate(['/']);
  }

  masqueradeSwitchBack(): void {

    const previousToken = this.getPreviousAccessToken();
    if (!previousToken) {
      return;
    }
    this.clearAllCookies();

    const cookies = document.cookie.split(';');

    for (const cookie of cookies) {
      const eqPos = cookie.indexOf('=');
      const name = eqPos > -1 ? cookie.substring(0, eqPos) : cookie;

      if (
        [
          StorageToken.masquerade.trim(),
          StorageToken.previousAccessToken.trim(),
          'previous_access_token_1',
          'previous_access_token_2',
          StorageToken.masRefreshToken.trim(),
          StorageToken.masqueradePermissions.trim(),
          StorageToken.masAccessToken.trim(),
          StorageToken.accessToken.trim(),
        ].includes(name.trim())
      ) {
        document.cookie = name + '=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';
      }
    }
    this.clearPermissionCookie();
    this.storageService.deleteFromLocalStorage(StorageToken.masqueradePermissions);
    this.setAccessToken(previousToken);
    this.setUserDetails(this.authService.accessToken);
    const permissionToken = localStorage.getItem('np_permission_token');
    if (permissionToken) {
      const payload = this.jwtHelperService.decodeToken(permissionToken);
      this.userService.setUserPermissions(payload?.permissions || []);
    }
    this.helperService.showMessage('info', 'Switch Backed As a LoggedIn User');
    this.router.navigate(['/']);
  }

  clearPermissionCookie(): void {
    let len = 2;
    for (let i = 1; i < len; i++) {
      let str = 'mas-permissions_' + i;
      const cookieValue = this.storageService.getCookie(str);
      if (cookieValue) {
        document.cookie = str + '=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';
        len++;
      } else {
        len = 0;
      }
    }
  }


  setAccessToken(token: string): void {
    const options: any = {
      path: '/',
      secure: window.location.protocol === 'https:',
      sameSite: 'Lax',
      expires: 1,
    };

    this.storageService.deleteCookie(StorageToken.accessToken, '/');
    this.storageService.deleteCookie('access_token_1', '/');
    this.storageService.deleteCookie('access_token_2', '/');
    this.storageService.deleteCookie('tokenSplit', '/');

    if (token.length > ACCESS_TOKEN_SPLIT_THRESHOLD) {
      const mid = Math.ceil(token.length / 2);
      this.storageService.addCookie('access_token_1', token.slice(0, mid), options);
      this.storageService.addCookie('access_token_2', token.slice(mid), options);
      this.storageService.addCookie('tokenSplit', 'true', options);
    } else {
      this.storageService.addCookie(StorageToken.accessToken, token, options);
      this.storageService.addCookie('tokenSplit', 'false', options);
    }
  }

  applyRefreshedTokens(headers: HttpHeaders): void {
    const newAccessToken = headers.get('masquerade-access-token');
    if (newAccessToken) {
      this.setAccessToken(newAccessToken);
      this.storageService.addCookie(StorageToken.masAccessToken, newAccessToken, {
        path: '/',
        secure: window.location.protocol === 'https:',
        sameSite: 'Lax',
        expires: 1,
      });
    }

    const newRefreshToken = headers.get('masquerade-refresh-token');
    if (newRefreshToken) {
      this.storageService.addCookie(StorageToken.masRefreshToken, newRefreshToken, {
        path: '/',
        secure: window.location.protocol === 'https:',
        sameSite: 'Lax',
      });
    }
  }

  clearAllCookies() {
    const cookies = document.cookie.split(';');

    for (const cookie of cookies) {
      const eqPos = cookie.indexOf('=');
      const name = eqPos > -1 ? cookie.substring(0, eqPos) : cookie;

      const trimmed = name.trim();
      if (
        trimmed === 'access_token' ||
        trimmed === 'access_token_1' ||
        trimmed === 'access_token_2' ||
        trimmed === 'tokenSplit' ||
        trimmed === StorageToken.masqueradePermissions.trim() ||
        trimmed.startsWith('mas-permissions_')
      ) {
        document.cookie = name + '=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';
      }
    }
  }


  setPreviousAccessToken(): void {
    const prevTokenValue = this.authService.accessToken;
    const options: any = {
      path: '/',
      secure: window.location.protocol === 'https:',
      sameSite: 'Lax',
    };

    this.storageService.deleteCookie(StorageToken.previousAccessToken, '/');
    this.storageService.deleteCookie('previous_access_token_1', '/');
    this.storageService.deleteCookie('previous_access_token_2', '/');

    if (!prevTokenValue) {
      return;
    }

    if (prevTokenValue.length > ACCESS_TOKEN_SPLIT_THRESHOLD) {
      const mid = Math.ceil(prevTokenValue.length / 2);
      this.storageService.addCookie('previous_access_token_1', prevTokenValue.slice(0, mid), options);
      this.storageService.addCookie('previous_access_token_2', prevTokenValue.slice(mid), options);
    } else {
      this.storageService.addCookie(StorageToken.previousAccessToken, prevTokenValue, options);
    }
  }

  setUserDetails(token: string): void {
    const userDetails = this.jwtHelperService.decodeToken(token);
    if (userDetails) {
      if (userDetails.preferred_username == null && userDetails.username != null) {
        userDetails.preferred_username = userDetails.username;
      }
      if (userDetails.resource_access == null) {
        userDetails.resource_access = { 'nephroplus-bo': { roles: userDetails['cognito:groups'] || [] } };
      }
    }
    this.userService.setUserDetails$(userDetails);
  }
}
